import { Switch, Route, Router as WouterRouter } from "wouter";
import { lazy, Suspense } from "react";
import type { ComponentType } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Layout } from "@/components/layout/Layout";
import NotFound from "@/pages/not-found";

import Home from "@/pages/home";
import LawnCareServer from "@/pages/lawn-care";
import LandscapingServer from "@/pages/landscaping";
import GardenAndPlantingServer from "@/pages/garden-and-planting";
import LandscapeRenovationServer from "@/pages/landscape-renovation";
import TreeAndShrubCareServer from "@/pages/tree-and-shrub-care";
import StoneworkServer from "@/pages/stonework";
import CommercialServicesServer from "@/pages/commercial-services";
import SkidSteerServicesServer from "@/pages/skid-steer-services";
import SnowPlowingServer from "@/pages/snow-plowing";
import FranklinServer from "@/pages/service-areas/franklin";
import BellinghamServer from "@/pages/service-areas/bellingham";
import MedwayServer from "@/pages/service-areas/medway";
import NorfolkServer from "@/pages/service-areas/norfolk";
import WrenthamServer from "@/pages/service-areas/wrentham";
import GalleryServer from "@/pages/gallery";
import ContactServer from "@/pages/contact";
import PrivacyServer from "@/pages/privacy";
import GBPCoverServer from "@/pages/gbp-cover";

const queryClient = new QueryClient();

function staticPage(Page: ComponentType) {
  return function StaticPage() {
    return <Suspense fallback={null}><Page /></Suspense>;
  };
}

function lazyClientPage(loader: () => Promise<{ default: ComponentType }>) {
  const LazyPage = lazy(loader);
  return function LazyClientPage() {
    return <Suspense fallback={null}><LazyPage /></Suspense>;
  };
}

const page = (
  server: ComponentType,
  loader: () => Promise<{ default: ComponentType }>,
) => import.meta.env.SSR ? staticPage(server) : lazyClientPage(loader);

const LawnCare = page(LawnCareServer, () => import("@/pages/lawn-care"));
const Landscaping = page(LandscapingServer, () => import("@/pages/landscaping"));
const GardenAndPlanting = page(GardenAndPlantingServer, () => import("@/pages/garden-and-planting"));
const LandscapeRenovation = page(LandscapeRenovationServer, () => import("@/pages/landscape-renovation"));
const TreeAndShrubCare = page(TreeAndShrubCareServer, () => import("@/pages/tree-and-shrub-care"));
const Stonework = page(StoneworkServer, () => import("@/pages/stonework"));
const CommercialServices = page(CommercialServicesServer, () => import("@/pages/commercial-services"));
const SkidSteerServices = page(SkidSteerServicesServer, () => import("@/pages/skid-steer-services"));
const SnowPlowing = page(SnowPlowingServer, () => import("@/pages/snow-plowing"));
const Franklin = page(FranklinServer, () => import("@/pages/service-areas/franklin"));
const Bellingham = page(BellinghamServer, () => import("@/pages/service-areas/bellingham"));
const Medway = page(MedwayServer, () => import("@/pages/service-areas/medway"));
const Norfolk = page(NorfolkServer, () => import("@/pages/service-areas/norfolk"));
const Wrentham = page(WrenthamServer, () => import("@/pages/service-areas/wrentham"));
const Gallery = page(GalleryServer, () => import("@/pages/gallery"));
const Contact = page(ContactServer, () => import("@/pages/contact"));
const Privacy = page(PrivacyServer, () => import("@/pages/privacy"));
const GBPCover = page(GBPCoverServer, () => import("@/pages/gbp-cover"));

function Router() {
  return (
    <Switch>
      <Route path="/gbp-cover" component={GBPCover} />
      <Route>{() => (
        <Layout>
          <Switch>
            <Route path="/" component={Home} />
            <Route path="/lawn-care" component={LawnCare} />
            <Route path="/landscaping" component={Landscaping} />
            <Route path="/garden-and-planting" component={GardenAndPlanting} />
            <Route path="/landscape-renovation" component={LandscapeRenovation} />
            <Route path="/tree-and-shrub-care" component={TreeAndShrubCare} />
            <Route path="/stonework" component={Stonework} />
            <Route path="/commercial-services" component={CommercialServices} />
            <Route path="/commercial-services/skid-steer-services" component={SkidSteerServices} />
            <Route path="/snow-plowing" component={SnowPlowing} />
            <Route path="/service-areas/franklin" component={Franklin} />
            <Route path="/service-areas/bellingham" component={Bellingham} />
            <Route path="/service-areas/medway" component={Medway} />
            <Route path="/service-areas/norfolk" component={Norfolk} />
            <Route path="/service-areas/wrentham" component={Wrentham} />
            <Route path="/gallery" component={Gallery} />
            <Route path="/contact" component={Contact} />
            <Route path="/privacy" component={Privacy} />
            <Route component={NotFound} />
          </Switch>
        </Layout>
      )}</Route>
    </Switch>
  );
}

export default function App({ ssrPath }: { ssrPath?: string } = {}) {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")} ssrPath={ssrPath}>
          <Router />
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}